/**
 * Shared snippet list component. Used by both the slide-in drawer
 * (inside the blog editor) and the Settings → Blog Snippets page.
 *
 * The rendered HTML is inert — the consumer attaches event delegation
 * for data-snippet-action handlers: edit | duplicate | delete | toggle-active
 */
import { escapeHtml } from "./escapeHtml.js";

const POSITION_LABELS = {
    end: 'end',
    start: 'start',
    before_cta: 'before CTA',
    replace_cta: 'replace CTA',
    after_first_h2: 'after first H2',
};

export function renderSnippetList(snippets, { compact = false } = {}) {
    if (!snippets || snippets.length === 0) {
        return `<div class="bs-list-empty">No snippets yet. Create one to reuse boilerplate HTML across every blog post.</div>`;
    }
    return `
        <div class="bs-list ${compact ? 'bs-list--compact' : ''}">
            ${snippets.map((s) => renderRow(s, compact)).join('')}
        </div>
    `;
}

function renderRow(s, compact) {
    const scopeBadge = s.scope === 'global' || s.brand_id == null
        ? '<span class="bs-scope-badge bs-scope-badge--global" title="Applies to every brand">🌐 Global</span>'
        : '<span class="bs-scope-badge bs-scope-badge--brand" title="This brand only">🎨 Brand</span>';
    const autoBadge = s.auto_apply
        ? '<span class="bs-auto-badge bs-auto-badge--on">auto</span>'
        : '<span class="bs-auto-badge bs-auto-badge--off">manual</span>';
    const position = POSITION_LABELS[s.position] || s.position || 'end';
    const desc = s.description ? escapeHtml(s.description) : '';

    return `
        <div class="bs-row" data-snippet-id="${escapeHtml(s.id)}">
            <div class="bs-row-head">
                <div class="bs-row-name">${escapeHtml(s.name)}</div>
                <div class="bs-row-badges">${scopeBadge} ${autoBadge} <span class="bs-position-chip">${escapeHtml(position)}</span></div>
            </div>
            ${desc && !compact ? `<div class="bs-row-desc">${desc}</div>` : ''}
            <div class="bs-row-actions">
                <button class="bs-row-btn" data-snippet-action="edit" data-snippet-id="${escapeHtml(s.id)}">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    Edit
                </button>
                <button class="bs-row-btn" data-snippet-action="toggle-auto" data-snippet-id="${escapeHtml(s.id)}" data-current="${s.auto_apply ? '1' : '0'}">
                    ${s.auto_apply ? 'Disable auto' : 'Enable auto'}
                </button>
                <button class="bs-row-btn bs-row-btn--danger" data-snippet-action="delete" data-snippet-id="${escapeHtml(s.id)}">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                    Delete
                </button>
            </div>
        </div>
    `;
}
